import type { ReactNode } from 'react';
import { CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  TIME_PERIOD_LABEL,
  type TimePeriod,
  type TimePeriodKey,
  daysFromCustomRange,
  timePeriodFromKey,
} from './timePeriod';
import { formatLastUpdated } from './freshness';

/**
 * Shared widget-card header consumed by every dashboard tile. Wraps
 * Shadcn `CardHeader` + `CardTitle` and layers in three optional bits of
 * dashboard chrome — a per-widget time-period selector (AC 12.1.10), a
 * freshness indicator (AC 12.1.1), and a right-slot for widget-specific
 * decorations like the supervisor "amber pulse" status dot.
 *
 * Time-period selector: when supplied, the selector is rendered to the
 * right of the title. The `period` value is the source of truth; the
 * widget passes `period.days` straight to its underlying service
 * function. Picking "Custom Range" reveals two date inputs that compute
 * a day count from the inclusive range — service functions are not
 * extended for this session, so the custom range simply collapses back
 * to a `days` integer at the call site.
 *
 * Freshness indicator: pass `lastUpdated` from `useQuery().dataUpdatedAt`
 * (epoch ms) so the displayed timestamp reflects actual cache freshness.
 */

export type WidgetCardHeaderSize = 'compact' | 'normal';

export interface WidgetCardHeaderProps {
  title: string;
  // 'compact' = `text-xs muted` (matches the existing single-headline
  // tiles like Operations Status / Active DSRs Today).
  // 'normal' = `text-sm` (matches the existing wider cards like
  // SSE Distribution / Project Breakdown).
  size?: WidgetCardHeaderSize;
  period?: TimePeriod;
  onPeriodChange?: (next: TimePeriod) => void;
  lastUpdated?: number | null;
  rightSlot?: ReactNode;
  className?: string;
}

export function WidgetCardHeader({
  title,
  size = 'compact',
  period,
  onPeriodChange,
  lastUpdated,
  rightSlot,
  className,
}: WidgetCardHeaderProps) {
  const titleClass =
    size === 'compact'
      ? 'text-xs font-medium text-muted-foreground'
      : 'text-sm';
  const freshness = formatLastUpdated(lastUpdated);
  const showSelector = !!period && !!onPeriodChange;
  const showCustom = period?.key === 'custom';

  return (
    <CardHeader className={className ?? 'pb-2'}>
      <div className="flex items-start justify-between gap-2">
        <CardTitle className={titleClass}>{title}</CardTitle>
        <div className="flex items-center gap-2">
          {rightSlot}
          {showSelector && (
            <TimePeriodSelector period={period!} onChange={onPeriodChange!} />
          )}
        </div>
      </div>
      {(showCustom || freshness) && (
        <div className="mt-1 flex items-center justify-between gap-2">
          {showCustom ? (
            <CustomRangeInputs period={period!} onChange={onPeriodChange!} />
          ) : (
            <span />
          )}
          {freshness && (
            <span className="text-[10px] text-muted-foreground">
              {freshness}
            </span>
          )}
        </div>
      )}
    </CardHeader>
  );
}

function TimePeriodSelector({
  period,
  onChange,
}: {
  period: TimePeriod;
  onChange: (next: TimePeriod) => void;
}) {
  return (
    <Select
      value={period.key}
      onValueChange={(v) => onChange(timePeriodFromKey(v as TimePeriodKey, period))}
    >
      <SelectTrigger className="h-7 w-[8.5rem] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="today">{TIME_PERIOD_LABEL.today}</SelectItem>
        <SelectItem value="7d">{TIME_PERIOD_LABEL['7d']}</SelectItem>
        <SelectItem value="30d">{TIME_PERIOD_LABEL['30d']}</SelectItem>
        <SelectItem value="90d">{TIME_PERIOD_LABEL['90d']}</SelectItem>
        <SelectItem value="custom">{TIME_PERIOD_LABEL.custom}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function CustomRangeInputs({
  period,
  onChange,
}: {
  period: TimePeriod;
  onChange: (next: TimePeriod) => void;
}) {
  const update = (field: 'customFrom' | 'customTo', value: string) => {
    const merged = { ...period, [field]: value };
    const days = daysFromCustomRange(merged.customFrom, merged.customTo);
    onChange({
      ...merged,
      days: days ?? period.days,
    });
  };
  return (
    <div className="flex items-center gap-1">
      <Input
        type="date"
        aria-label="Custom range start"
        value={period.customFrom ?? ''}
        onChange={(e) => update('customFrom', e.target.value)}
        className="h-6 w-[7.5rem] px-1.5 text-[11px]"
      />
      <span className="text-[10px] text-muted-foreground">→</span>
      <Input
        type="date"
        aria-label="Custom range end"
        value={period.customTo ?? ''}
        onChange={(e) => update('customTo', e.target.value)}
        className="h-6 w-[7.5rem] px-1.5 text-[11px]"
      />
    </div>
  );
}
